// Settings overlay header identity — real org branding.
//
// The overlay header used to print the maket persona profile
// ("LeatherCraftLab · 3 stores · Sarah Johnson (BO2) · Etsy + Shopify +
// Amazon") even in the real engine (known backlog: seller shell header mock
// branding). In real mode this component now reads:
//   - org name        → GET /orgs/{org_id} (org_id from /auth/me
//                       active_membership; same query key as RealOrganization
//                       so the cache is shared)
//   - store count +   → the shared ['store-integrations'] query (sellers
//     provider list     only), counting known connectable providers incl. eBay
// Maket/simulation mode keeps the persona profile — the mock role-switcher IS
// the identity there (non-big-bang cutover pattern, same as Organization.tsx).

import { useQuery } from '@tanstack/react-query'
import { API_BASE, USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { useT } from '../../i18n'
import { settingsShellI18n } from '../../i18n/settings/shell'
import {
  STORE_INTEGRATIONS_QUERY_KEY,
  fetchStoreIntegrations,
} from '../seller/stores/store-integrations-query'
import { getStoreProvider } from '../seller/stores/store-providers'
import { orgProfileFor } from '../../lib/maket/org-profile'
import type { SettingsSurface } from './Settings'

type OrgRead = { id: string; name: string }

async function fetchOrg(orgId: string): Promise<OrgRead> {
  const response = await fetch(`${API_BASE}/orgs/${orgId}`)
  if (!response.ok) throw new Error(`/orgs/${orgId} ${response.status}`)
  return response.json() as Promise<OrgRead>
}

function RealIdentity({ surface, themeAware }: { surface: SettingsSurface; themeAware: boolean }) {
  const t = useT(settingsShellI18n)
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe })
  const membership = me.data?.active_membership ?? null
  const orgId = membership?.org_id ?? null

  const org = useQuery({
    queryKey: ['orgs', orgId],
    queryFn: () => fetchOrg(orgId as string),
    enabled: Boolean(orgId),
  })

  const stores = useQuery({
    queryKey: STORE_INTEGRATIONS_QUERY_KEY,
    queryFn: fetchStoreIntegrations,
    enabled: surface === 'seller',
  })

  const name = org.data?.name ?? '—'

  let subline = '—'
  if (surface === 'seller') {
    if (stores.data) {
      const providers = stores.data.items
        .map((store) => getStoreProvider(store.provider))
        .filter(
          (provider): provider is NonNullable<typeof provider> =>
            Boolean(provider) && provider?.availability === 'connectable',
        )
      const providerNames = [...new Set(providers.map((p) => p.name))]
      subline =
        providers.length === 0
          ? t.header.noStores
          : `${t.header.storeCount(providers.length)} · ${providerNames.join(' + ')}`
    }
  } else if (membership) {
    subline = membership.persona_label ?? membership.org_type ?? '—'
  }

  return (
    <div className="min-w-0">
      <h1 className={`truncate text-base font-semibold ${themeAware ? 'text-[var(--color-text)]' : 'text-slate-900'}`}>{name}</h1>
      <p className={`truncate text-xs ${themeAware ? 'text-[var(--color-text-muted)]' : 'text-slate-500'}`}>{subline}</p>
    </div>
  )
}

export function SettingsHeaderIdentity({
  surface,
  themeAware = false,
}: {
  surface: SettingsSurface
  themeAware?: boolean
}) {
  if (USE_REAL_API) return <RealIdentity surface={surface} themeAware={themeAware} />

  const profile = orgProfileFor(surface)
  return (
    <div className="min-w-0">
      <h1 className={`truncate text-base font-semibold ${themeAware ? 'text-[var(--color-text)]' : 'text-slate-900'}`}>
        {profile.display_name}
      </h1>
      <p className={`truncate text-xs ${themeAware ? 'text-[var(--color-text-muted)]' : 'text-slate-500'}`}>
        {profile.scope_label} · {profile.scope_sublabel}
      </p>
    </div>
  )
}
